<template>
  <v-container fluid>
    <div class="d-flex align-center flex-wrap mb-4" style="gap:12px;">
      <div>
        <div class="text-h6 font-weight-bold primary--text">{{ entityName }}</div>
        <div class="text-caption grey--text text--darken-1">
          Periode KPI: {{ periodLabel }} &middot; Bulan Berjalan: {{ activeMonthLabel }}
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn small outlined color="primary" :loading="loading" @click="fetchSummary">
        <v-icon left small>mdi-refresh</v-icon> Refresh
      </v-btn>
    </div>

    <div
      v-if="summary"
      class="dash-window-banner mb-4"
      :class="inputWindow.is_open ? 'dash-window-banner--open' : 'dash-window-banner--closed'"
    >
      <div class="dash-window-banner__icon">
        <v-icon :color="inputWindow.is_open ? 'success' : 'blue-grey darken-1'">
          {{ inputWindow.is_open ? 'mdi-lock-open-variant' : 'mdi-lock' }}
        </v-icon>
      </div>
      <div class="dash-window-banner__text">
        Jendela input KPI setiap tanggal {{ inputWindow.start_date }}–{{ inputWindow.end_date }} —
        <strong>{{ inputWindow.is_open ? 'sedang dibuka' : 'sedang ditutup' }}</strong>.
      </div>
      <v-spacer></v-spacer>
      <div v-if="inputWindow.is_open && countdown" class="dash-countdown">
        <v-icon small color="success" class="mr-2">mdi-timer-outline</v-icon>
        <span class="dash-countdown__label mr-2">Menuju penutupan</span>
        <span class="dash-countdown__value">
          <template v-if="countdown.days > 0">{{ countdown.days }} Hari&nbsp;</template>{{ pad(countdown.hours) }}:{{ pad(countdown.minutes) }}:{{ pad(countdown.seconds) }}
        </span>
      </div>
    </div>

    <div v-if="loading && !summary" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
    </div>

    <template v-else-if="summary">
      <v-row dense>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined class="dash-stat-card">
            <v-icon color="primary" size="26">mdi-file-tree</v-icon>
            <div class="dash-stat-card__value">{{ stats.total_chart }}</div>
            <div class="dash-stat-card__label">Total Posisi</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined class="dash-stat-card">
            <v-icon color="green" size="26">mdi-check-circle</v-icon>
            <div class="dash-stat-card__value">{{ stats.published }}</div>
            <div class="dash-stat-card__label">Published</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined class="dash-stat-card">
            <v-icon color="grey darken-1" size="26">mdi-circle-outline</v-icon>
            <div class="dash-stat-card__value">{{ stats.unpublished }}</div>
            <div class="dash-stat-card__label">Belum Publish</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined class="dash-stat-card">
            <v-icon color="red lighten-1" size="26">mdi-account-off</v-icon>
            <div class="dash-stat-card__value">{{ stats.vacant }}</div>
            <div class="dash-stat-card__label">Posisi Kosong</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined class="dash-stat-card">
            <v-icon color="indigo" size="26">mdi-chart-line</v-icon>
            <div class="dash-stat-card__value">{{ stats.avg_score_current !== null ? stats.avg_score_current : '-' }}</div>
            <div class="dash-stat-card__label">Rata-rata Score Bulan Ini</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card outlined class="dash-stat-card">
            <v-icon color="teal" size="26">mdi-progress-check</v-icon>
            <div class="dash-stat-card__value">{{ stats.fill_rate_percent }}%</div>
            <div class="dash-stat-card__label">Pengisian Bulan Ini</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12" md="8">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-subtitle-2 font-weight-medium mb-3">
              <v-icon small color="primary" class="mr-1">mdi-chart-areaspline</v-icon>
              Tren Rata-rata Score Bulanan
            </div>
            <div v-if="!hasTrendData" class="text-center grey--text py-10">
              <v-icon color="grey lighten-1" size="36">mdi-chart-line-variant</v-icon>
              <div class="mt-2 caption">Belum ada data score untuk periode ini.</div>
            </div>
            <div v-show="hasTrendData" style="position:relative; height:280px;">
              <canvas id="scoreTrendChart"></canvas>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 fill-height">
            <div class="text-subtitle-2 font-weight-medium mb-3">
              <v-icon small color="red" class="mr-1">mdi-alert-decagram-outline</v-icon>
              Perlu Perhatian (Score Terendah)
            </div>
            <div v-if="!attention.length" class="text-center grey--text py-8">
              <v-icon color="grey lighten-1" size="40">mdi-emoticon-happy-outline</v-icon>
              <div class="mt-2 caption">
                {{ stats.filled_count === 0 ? 'Belum ada data score bulan ini.' : 'Semua posisi memiliki performa baik.' }}
              </div>
            </div>
            <v-list v-else dense class="py-0">
              <v-list-item v-for="row in attention" :key="row.chart_id" class="px-0">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{ row.position_name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ row.user_name || 'Belum diisi' }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small :color="scoreColor(row.score_current)" dark>{{ row.score_current }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <div v-else-if="!loading" class="text-center grey--text py-12">
      <v-icon size="48" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
      <div class="mt-2">Gagal memuat data dashboard.</div>
    </div>
  </v-container>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from "chart.js";

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);

export default {
  name: "Dashboard",
  data() {
    return {
      loading: false,
      summary: null,
      chartInstance: null,
      countdown: null,
      countdownTimer: null,
    };
  },
  computed: {
    entityName() {
      return this.summary?.entity?.name || "-";
    },
    periodLabel() {
      return this.summary?.period?.label || "Belum ada periode aktif";
    },
    activeMonthLabel() {
      return this.summary?.active_month?.label || "-";
    },
    stats() {
      return this.summary?.stats || {};
    },
    inputWindow() {
      return this.summary?.input_window || {};
    },
    attention() {
      return this.summary?.attention || [];
    },
    hasTrendData() {
      const data = this.summary?.score_trend?.data || [];
      return data.some((v) => v !== null);
    },
  },
  mounted() {
    this.fetchSummary();
    // Entity dipindah lewat selector di navbar atas (NavigationHeaderDefault.vue) ->
    // reload halaman ini biar seluruh ringkasan (stat, tren, perlu perhatian) fresh
    // untuk entity yang baru dipilih. Konsisten dengan pola di halaman lain
    // (SetupListView, SuperSetup, AdminSetup, dll).
    this.$root.$off("entity-updated");
    this.$root.$on("entity-updated", () => window.location.reload());
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
    this.stopCountdown();
  },
  methods: {
    async fetchSummary() {
      this.loading = true;
      try {
        const res = await this.$axios.get(`${process.env.VUE_APP_URL}/api/dashboard/summary`, {
          headers: { Authorization: `Bearer ${this.$store.getters.isTokenUser}` },
        });
        this.summary = res.data;
        this.$nextTick(() => this.renderTrendChart());
        this.startCountdown();
      } catch (err) {
        console.error("Gagal memuat dashboard:", err);
      } finally {
        this.loading = false;
      }
    },

    startCountdown() {
      this.stopCountdown();
      const closesAt = this.inputWindow?.closes_at;
      if (!this.inputWindow?.is_open || !closesAt) {
        this.countdown = null;
        return;
      }

      const target = new Date(closesAt).getTime();
      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
          this.stopCountdown();
          return;
        }
        const totalSeconds = Math.floor(diff / 1000);
        this.countdown = {
          days: Math.floor(totalSeconds / 86400),
          hours: Math.floor((totalSeconds % 86400) / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60,
        };
      };
      tick();
      this.countdownTimer = setInterval(tick, 1000);
    },

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    pad(n) {
      return String(n).padStart(2, "0");
    },

    renderTrendChart() {
      const canvas = document.getElementById("scoreTrendChart");
      if (!canvas || !this.hasTrendData) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const trend = this.summary.score_trend;
      this.chartInstance = new Chart(canvas.getContext("2d"), {
        type: "line",
        data: {
          labels: trend.labels,
          datasets: [
            {
              label: "Rata-rata Score",
              data: trend.data,
              borderColor: "#1976d2",
              backgroundColor: "rgba(25,118,210,0.12)",
              fill: true,
              tension: 0.3,
              spanGaps: true,
              pointRadius: 3,
              pointBackgroundColor: "#1976d2",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, suggestedMax: 100 } },
        },
      });
    },

    scoreColor(score) {
      if (score >= 80) return "green";
      if (score >= 60) return "orange";
      return "red";
    },
  },
};
</script>

<style scoped>
.dash-stat-card {
  padding: 16px;
  text-align: center;
  border-radius: 10px;
  height: 100%;
}
.dash-stat-card__value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 6px;
  line-height: 1.2;
}
.dash-stat-card__label {
  font-size: 0.72rem;
  color: #757575;
  margin-top: 2px;
}

.dash-window-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.dash-window-banner--open {
  border-color: rgba(76, 175, 80, 0.3);
  background: linear-gradient(to right, rgba(76, 175, 80, 0.06), #fff 65%);
}
.dash-window-banner--closed {
  border-color: rgba(96, 125, 139, 0.25);
  background: linear-gradient(to right, rgba(96, 125, 139, 0.06), #fff 65%);
}
.dash-window-banner__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dash-window-banner--open .dash-window-banner__icon {
  background: rgba(76, 175, 80, 0.14);
}
.dash-window-banner--closed .dash-window-banner__icon {
  background: rgba(96, 125, 139, 0.14);
}
.dash-window-banner__text {
  font-size: 0.875rem;
  color: #424242;
}

.dash-countdown {
  display: flex;
  align-items: center;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  padding: 6px 14px;
}
.dash-countdown__label {
  font-size: 0.7rem;
  color: #2e7d32;
  opacity: 0.85;
  white-space: nowrap;
}
.dash-countdown__value {
  font-family: "Roboto Mono", "Courier New", monospace;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.5px;
  color: #2e7d32;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .dash-window-banner {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 14px;
  }
  .dash-window-banner__text {
    flex: 1;
    min-width: 0;
  }
  .dash-window-banner > .spacer {
    display: none;
  }
  .dash-countdown {
    width: 100%;
    justify-content: center;
  }
}
</style>
